import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, TrendingUp, Globe, BarChart3, Target, Zap, CheckCircle, ArrowUpRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const SEOPortfolio = () => {
  const project = {
    title: 'TechStore - SEO Optimizasyonu',
    client: 'TechStore E-Ticaret',
    duration: '8 Ay',
    category: 'SEO & Dijital Büyüme',
    year: '2025',
    heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
    description: 'TechStore e-ticaret sitesi için kapsamlı SEO stratejisi geliştirdik. Teknik SEO, içerik optimizasyonu ve link building çalışmalarıyla organik trafiği %340 artırdık.',
    challenge: 'TechStore, yoğun rekabet ortamında arama motorlarında görünürlük kazanmakta zorlanıyordu. Site teknik sorunlar içeriyordu ve içerik stratejisi eksikti.',
    solution: 'Kapsamlı bir SEO denetimi yaparak teknik sorunları giderdik. Anahtar kelime araştırması ile içerik stratejisi oluşturduk ve otorite kazanmak için backlink kampanyaları yürüttük.'
  };

  const stats = [
    { label: 'Organik Trafik Artışı', value: '+340%', icon: TrendingUp },
    { label: 'Anahtar Kelime Sıralaması', value: '850+', icon: Search },
    { label: 'Domain Authority', value: '58', icon: Globe },
    { label: 'Dönüşüm Artışı', value: '+125%', icon: Target }
  ];

  const keywordRankings = [
    { keyword: 'laptop fiyatları', before: 45, after: 3, volume: '12.5K' },
    { keyword: 'gaming bilgisayar', before: 67, after: 5, volume: '8.2K' },
    { keyword: 'akıllı telefon', before: 89, after: 7, volume: '22.1K' },
    { keyword: 'tablet kampanya', before: 34, after: 2, volume: '5.8K' },
    { keyword: 'kulaklık önerileri', before: 56, after: 4, volume: '9.3K' }
  ];

  const seoServices = [
    { title: 'Teknik SEO', items: ['Site hızı optimizasyonu', 'Mobile-first indexing', 'Schema markup', 'XML sitemap'] },
    { title: 'İçerik SEO', items: ['Anahtar kelime araştırması', 'İçerik optimizasyonu', 'Blog stratejisi', 'Meta tag optimizasyonu'] },
    { title: 'Off-Page SEO', items: ['Link building', 'Guest posting', 'Dijital PR', 'Sosyal sinyaller'] },
    { title: 'Lokal SEO', items: ['Google My Business', 'Yerel dizinler', 'Review yönetimi', 'NAP tutarlılığı'] }
  ];

  const timeline = [
    { month: 'Ay 1-2', title: 'Denetim & Strateji', desc: 'Kapsamlı SEO denetimi ve strateji planlaması' },
    { month: 'Ay 3-4', title: 'Teknik İyileştirmeler', desc: 'Site hızı, mobil uyumluluk ve teknik düzeltmeler' },
    { month: 'Ay 5-6', title: 'İçerik Optimizasyonu', desc: 'Mevcut içeriklerin optimize edilmesi ve yeni içerik üretimi' },
    { month: 'Ay 7-8', title: 'Link Building', desc: 'Kaliteli backlink kampanyaları ve otorite artışı' }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/?scrollTo=portfolio"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Portfolyoya Dön
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm font-medium rounded-full">
                  {project.category}
                </span>
                <span className="text-gray-500">{project.year}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {project.title}
              </h1>

              <p className="text-xl text-gray-400 mb-8">{project.description}</p>

              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Müşteri</span>
                  <p className="text-white font-medium">{project.client}</p>
                </div>
                <div>
                  <span className="text-gray-500">Süre</span>
                  <p className="text-white font-medium">{project.duration}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src={project.heroImage}
                alt={project.title}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 shadow-xl">
                <Search className="w-8 h-8 text-white mb-2" />
                <div className="text-white font-bold text-2xl">#1</div>
                <div className="text-green-100 text-sm">Google Sıralaması</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 text-center hover:border-green-500/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Keyword Rankings */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Anahtar Kelime <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Sıralamaları</span>
          </h2>

          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-900/50 text-sm font-medium text-gray-400">
              <div>Anahtar Kelime</div>
              <div className="text-center">Önce</div>
              <div className="text-center">Sonra</div>
              <div className="text-center">Aylık Arama</div>
            </div>
            {keywordRankings.map((item, index) => (
              <div key={index} className="grid grid-cols-4 gap-4 p-4 border-t border-slate-700/50 items-center hover:bg-slate-800/50 transition-colors">
                <div className="text-white font-medium">{item.keyword}</div>
                <div className="text-center text-red-400">#{item.before}</div>
                <div className="text-center">
                  <span className="inline-flex items-center gap-1 text-green-400 font-bold">
                    #{item.after}
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
                <div className="text-center text-gray-400">{item.volume}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Zorluk</h3>
              <p className="text-gray-400">{project.challenge}</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Çözüm</h3>
              <p className="text-gray-400">{project.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Services Provided */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Uygulanan <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">SEO Hizmetleri</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {seoServices.map((service, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 hover:border-green-500/50 transition-all">
                <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Proje <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Zaman Çizelgesi</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {timeline.map((phase, index) => (
              <div key={index} className="relative">
                <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 hover:border-green-500/50 transition-all">
                  <div className="text-green-400 font-bold mb-2">{phase.month}</div>
                  <h3 className="text-lg font-bold text-white mb-2">{phase.title}</h3>
                  <p className="text-gray-400 text-sm">{phase.desc}</p>
                </div>
                {index < timeline.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-green-500 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Google'da
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent"> 1. Sıraya </span>
            Çıkın
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Web sitenizin SEO performansını artırmak için bizimle çalışın.
          </p>
          <Button 
            onClick={() => window.location.href = '/web-analiz'}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-6 text-lg rounded-xl"
          >
            Ücretsiz SEO Analizi Al
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SEOPortfolio;
